import { useMemo } from 'react';

import {
  DataTableFilterPanel,
  DataTableSaveFilterForm,
  DataTableSavedFiltersList,
  toast
} from 'design-system';

import type { DataTableFeatures, DataTableFilterPanelGroup } from 'design-system';

import type { CatalogItem } from '@/entities/catalog-item';

import { SAVED_FILTER_NAME_MAX_LENGTH } from '@/config/name';
import { NEW_DATA_URL_PARAM } from '@/features/catalog/constants';
import { useSavedFilters } from '@/features/catalog/hooks/useSavedFilters';
import { toSavedFilterCriteria } from '@/features/catalog/lib/savedFilters';
import { getTimeSince } from '@/shared/lib/date/getTimeSince';
import { useFilter } from '../../../state/useFilter';
import FilterOptionRow from './FilterOptionRow';

import type { ReactTable } from '@tanstack/react-table';

const TableFilters = ({
  groups,
  newDataCount,
  open,
  table
}: {
  groups: DataTableFilterPanelGroup<CatalogItem>[];
  newDataCount: number;
  open: boolean;
  table: ReactTable<DataTableFeatures, CatalogItem>;
}) => {
  const { filters, setFilter, clearAllFilters, applySavedFilter, isAnyFilterApplied } = useFilter();
  const { savedFilters, saveCurrentFilter, deleteFilter } = useSavedFilters();
  const existingFilterNames = useMemo(
    () => savedFilters.map(filter => filter.name.toLowerCase()),
    [savedFilters]
  );

  return (
    <DataTableFilterPanel
      footer={
        <DataTableSaveFilterForm
          canSave={isAnyFilterApplied}
          existingNames={existingFilterNames}
          onSave={name => {
            // `DataTableSaveFilterForm` only guards against empty/duplicate names —
            // preserve the max-length rule `createNameSchema` enforced previously.
            if (name.length > SAVED_FILTER_NAME_MAX_LENGTH) {
              toast.add({
                title: `Filter name must be ${SAVED_FILTER_NAME_MAX_LENGTH} characters or less`,
                type: 'error'
              });
              return;
            }
            saveCurrentFilter(name, toSavedFilterCriteria(filters));
          }}
        />
      }
      groups={groups}
      open={open}
      table={table}
      onClearAll={clearAllFilters}
    >
      <DataTableSavedFiltersList
        formatCreatedAt={getTimeSince}
        savedFilters={savedFilters}
        onApply={applySavedFilter}
        onDelete={deleteFilter}
      />
      <FilterOptionRow
        checked={filters[NEW_DATA_URL_PARAM]}
        count={newDataCount}
        label='New Data'
        showCount={false}
        onChange={() => {
          setFilter(NEW_DATA_URL_PARAM, !filters[NEW_DATA_URL_PARAM]);
        }}
      />
    </DataTableFilterPanel>
  );
};

export default TableFilters;
